'use client'
import React, { useState } from 'react'
import { useFormik } from 'formik'
import { signUpValidationSchema } from '@/src/validations/formvalidations'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import { EyeClosedIcon, EyeOpenIcon, GoogleSVG } from '@/src/ui/AllSVG'
import type { AuthView } from './LoginSignupIndex'
import { getClientCookieValue } from '@/src/utils/getClientCookieValue'
import { resolveEuropeanCountryCookieValue } from '@/src/utils/europeanCountryList'
import { useUserRegisterMutation } from '@/src/store/rtkQueries/userAuthApi'
import GoogleLogin from './GoogleLogin'
import Link from 'next/link'
import { getPrivacyPolicyPath, getTermsAndConditionsPath } from '@/src/routes/routes'
import type { VerifyEmailOtpSource } from './VerifyEmailOtpModal'
import { getFcmTokenCookie } from '@/src/utils/authCookies'

interface SignUpProps {
    navigate: (view: AuthView) => void
    onVerifyEmail: (email: string, source: VerifyEmailOtpSource) => void
}

export default function SignUp({ navigate, onVerifyEmail }: SignUpProps) {
    const country = getClientCookieValue('user_country_code');
    const ip = getClientCookieValue('user_ip');

    const [showPassword, setShowPassword] = useState(false)
    const [userRegister, { isLoading: isSubmitting }] = useUserRegisterMutation()

    const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            agreeToTerms: false,
        },
        validationSchema: signUpValidationSchema,
        onSubmit: async (values) => {
            const payload = {
                name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                password: values.password,
                agree_to_terms: values.agreeToTerms,
                country: resolveEuropeanCountryCookieValue(country ?? ''),
                ip: ip,
                fcm_token: getFcmTokenCookie(),
            }
            try {
                const response = await userRegister(payload)
                if (response.data) {
                    onVerifyEmail(values.email, 'signup')
                    toast.success('Account created successfully')
                }
            } catch (error) {
                console.log('error', error);
            }

        },
    })

    return (
        <>
            <div className="flex flex-col gap-6 w-full">
                {/* Heading */}
                <div className="space-y-2 text-body-color">
                    <h1 className="secondary_heading">Sign up <span className="font-secondary italic">Now</span></h1>
                    <p className="text-sm">By creating an account, I am also consenting to receive SMS, messages and emails.</p>
                </div>

                {/* Continue with Google */}
                <GoogleLogin />

                {/* Divider */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-muted-color" />
                    <span className="text-body-color/40">or</span>
                    <div className="flex-1 h-px bg-muted-color" />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* First + Last name */}
                    <div className="two_column_form">
                        <div className="input_container">
                            <label className="form_label">First name <span className="required_field">*</span></label>
                            <input
                                type="text"
                                name="firstName"
                                value={values.firstName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="form_input"
                            />
                            {errors.firstName && touched.firstName && (
                                <p className="error_text">{errors.firstName}</p>
                            )}
                        </div>
                        <div className="input_container">
                            <label className="form_label">Last name <span className="required_field">*</span></label>
                            <input
                                type="text"
                                name="lastName"
                                value={values.lastName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="form_input"
                            />
                            {errors.lastName && touched.lastName && (
                                <p className="error_text">{errors.lastName}</p>
                            )}
                        </div>
                    </div>

                    {/* Email */}
                    <div className="input_container">
                        <label className="form_label">Email address <span className="required_field">*</span></label>
                        <input
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="example@xyz.com"
                            className="form_input"
                        />
                        {errors.email && touched.email && (
                            <p className="error_text">{errors.email}</p>
                        )}
                    </div>

                    {/* Password */}
                    <div className="input_container">
                        <label className="form_label">Password <span className="required_field">*</span> </label>
                        <div className="input_icon_container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="form_input"
                            />
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPassword(p => !p)}
                                className="shrink-0 px-3 text-body-color/40 hover:text-body-color/70 transition-colors"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                            </button>
                        </div>
                        {errors.password && touched.password && (
                            <p className="error_text">{errors.password}</p>
                        )}
                    </div>

                    {/* Terms checkbox */}
                    <div className="flex items-start gap-2.5">
                        <input
                            id="agreeToTerms"
                            type="checkbox"
                            name="agreeToTerms"
                            checked={values.agreeToTerms}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="mt-0.5 w-4 h-4 accent-body-color shrink-0 cursor-pointer"
                        />
                        <label htmlFor="agreeToTerms" className="text-sm text-body-color/70 leading-snug cursor-pointer">
                            By creating an account, I agree to our{' '}
                            <Link href={getTermsAndConditionsPath()} target='_blank' className="text-body-color underline underline-offset-2 hover:opacity-70 transition-opacity">
                                Terms of use
                            </Link>{' '}
                            and{' '}
                            <Link href={getPrivacyPolicyPath()} target='_blank' className="text-body-color underline underline-offset-2 hover:opacity-70 transition-opacity">
                                Privacy Policy
                            </Link>
                        </label>
                    </div>
                    {errors.agreeToTerms && touched.agreeToTerms && (
                        <p className="error_text -mt-2">{errors.agreeToTerms}</p>
                    )}

                    {/* Submit */}
                    <Button
                        type="submit"
                        className="global_btn rounded_full w-full text-sm font-medium tracking-wide mt-2"
                        isDisabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating account...' : 'Continue'}
                    </Button>
                </form>

                {/* Switch to login */}
                <p className="text-sm text-center w-full text-body-color">
                    Already have an account?{' '}
                    <span
                        onClick={() => navigate('SignIn')}
                        className="accent_text"
                    >
                        Sign in
                    </span>
                </p>
            </div>
        </>
    )
}